import { Controller } from "@hotwired/stimulus"
import { fieldToken, fingerprintFields, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"

// A11Y-1 / BUTTON-4: the reason every card-dirty Save gives while the clean
// state disables it, unless the button declares its own through
// `data-card-dirty-disabled-reason`. shared/_card_form_actions stamps the same
// default server-side, so a Save rendered `disabled` explains itself on the
// first paint too — keep the two in step.
export const DEFAULT_DISABLED_REASON = SAVE_UNCHANGED_REASON
// Without a reason line, the accessible name keeps the visible label and
// appends the reason ("Save — Change a field first", #13003) so a screen reader
// hears what the button does AND why it is unavailable. A Save described by its
// reason line (shared/card_form_actions, INTERACTION-5) keeps its plain name:
// the line is its description, so the reason is read once.
const NAME_SEPARATOR = " — "

// Disables a card's submit button until any field in the form differs from its
// initial value, and re-disables it when the form is reverted to that initial
// state. One generic controller covers every settings card: plain inputs,
// selects, textareas, file inputs, "Copy from Profile" (which dispatches input
// events), and the shared composer island (which dispatches an input event on
// its hidden field) all bubble input/change events to the form, so no
// per-field wiring is needed.
//
// `data-card-dirty-unsaved-value="true"`: the form was re-rendered from a
// REFUSED submission (a 422 page render of a `local: true` form), so the
// values it shows are not the saved ones. The card starts dirty and stays so
// until a save succeeds — the baseline is the last SAVED state, never what the
// refused submission put back on screen.
export default class extends Controller {
  static targets = ["submit", "cancel", "hint"]
  static values = { unsaved: Boolean }

  connect() {
    this._boundSync = this._sync.bind(this)
    this.element.addEventListener("input", this._boundSync)
    this.element.addEventListener("change", this._boundSync)
    // Settings cards save via a turbo_stream response that only appends a
    // toast — the form is never re-rendered, so this controller never
    // reconnects. Without re-baselining here, the snapshot keeps the pre-save
    // values and Save stays enabled forever after a successful save. Failed
    // saves respond 422 (detail.success false) and must stay dirty.
    this._boundSubmitEnd = this._submitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this._boundSubmitEnd)
    // A modal-form-reset on the same modal stamps the pristine (loaded) form
    // back on dismiss: that IS the clean state, so take it as the baseline.
    this._boundRestored = this._restored.bind(this)
    this.element.addEventListener("modal-form-reset:restored", this._boundRestored)
    // Snapshot on the next frame, not synchronously: nested controllers that
    // connect after this one (calendar-availability, notification-settings)
    // normalize their hidden JSON inputs and build rows with extra inputs
    // during their own connect(). A synchronous snapshot would fingerprint the
    // pre-normalization markup and the card would read dirty forever (or never
    // read clean again after a revert).
    this._snapshotFrame = requestAnimationFrame(() => {
      this._baseline()
      this._sync()
    })
  }

  disconnect() {
    cancelAnimationFrame(this._snapshotFrame)
    this.element.removeEventListener("input", this._boundSync)
    this.element.removeEventListener("change", this._boundSync)
    this.element.removeEventListener("turbo:submit-end", this._boundSubmitEnd)
    this.element.removeEventListener("modal-form-reset:restored", this._boundRestored)
  }

  _restored() {
    if (this.snapshot === undefined) return
    this.unsavedValue = false
    this._baseline()
    this._sync()
  }

  _submitEnd(event) {
    if (!event.detail.success) return
    // Only the tracked form's save re-baselines. On wrapper mounts (e.g. the
    // provider card, where an independent Validate form shares the card) a
    // sibling form's success would otherwise silently adopt unsaved edits as
    // the new clean baseline.
    if (event.target !== this._trackedForm()) return

    this.unsavedValue = false
    this._baseline()
    this._sync()
  }

  // Snapshot each field's element + token so an individual field's baseline
  // can be patched without re-reading the rest of the form.
  _baseline() {
    this._snapshotEntries = this._fields().map((el) => ({ el, token: this._tokenFor(el) }))
    this.snapshot = this._snapshotEntries.map((e) => e.token).join("|")
  }

  // The form whose submission owns this card's dirty state: the submit
  // target's form (covers a button inside the form AND one linked via the
  // form="" attribute), else the element itself when mounted on a form.
  _trackedForm() {
    if (this.hasSubmitTarget && this.submitTarget.form) return this.submitTarget.form
    return this.element instanceof HTMLFormElement ? this.element : null
  }

  // A submit target inserted after the baseline (the carrier form's cloned
  // Add Product / Add Link rows) must adopt the current dirty state on
  // arrival — otherwise it renders enabled while the rest of the form's save
  // buttons sit disabled. Stimulus fires this for the initial targets too,
  // where _sync's no-baseline guard makes it a no-op.
  submitTargetConnected() {
    this._sync()
  }

  // Symmetric: the carrier form removes rows via plain element.remove() — no
  // input/change event fires — both for unsaved rows (Remove) and persisted
  // rows (deleteItem, whose DELETE already committed server-side). Prune the
  // departed row's fields from the baseline rather than re-baselining: a
  // wholesale re-baseline would absorb genuine unsaved edits elsewhere in the
  // form (the same bug class as the Trix async-init race), while pruning keeps
  // them dirty and lets a fully-persisted deletion read clean.
  submitTargetDisconnected() {
    if (!this._snapshotEntries) return
    const pruned = this._snapshotEntries.filter((entry) => entry.el.isConnected)
    if (pruned.length !== this._snapshotEntries.length) {
      this._snapshotEntries = pruned
      this.snapshot = pruned.map((e) => e.token).join("|")
    }
    this._sync()
  }

  // Whether the card differs from its baseline — the ONE comparison behind
  // BUTTON-9. The page's leave-without-saving guard (unsaved-changes on the
  // same form) asks this instead of keeping its own "was anything typed" flag,
  // so an edit that was undone neither enables Save nor prompts on leave
  // (#18256). Before the baseline frame nothing can have been edited yet.
  get dirty() {
    if (this.snapshot === undefined) return false
    return this.unsavedValue || this._serialize() !== this.snapshot
  }

  _sync() {
    // Events fired by nested controllers initializing before the snapshot
    // frame (e.g. calendar-availability's initial jsonInput sync) are not user
    // edits — there's no baseline to compare against yet.
    if (this.snapshot === undefined) return
    const clean = !this.dirty
    // Every submit target follows the dirty state — some cards carry extra
    // submit buttons inside repeatable rows (e.g. the carrier form's per-row
    // Save) that submit the same form as the footer button.
    this.submitTargets.forEach((el) => {
      // A Save whose request is in flight (aria-busy, BUTTON-8) is never
      // re-OPENED: an edit typed mid-request must not enable it and send a
      // second request — the lib/gate_reason rule. Closing goes through.
      if (!clean && el.getAttribute("aria-busy") === "true") {
        el.disabled = true
        return
      }
      el.disabled = clean
      this._syncDisabledReason(el, clean)
    })
    // Cancel is pointless when nothing changed — hide it until the card is dirty.
    this.cancelTargets.forEach((el) => el.classList.toggle("d-none", clean))
    // The reason line (INTERACTION-5, first #18893) says, in plain sight, why
    // Save is held; it empties and hides the moment an edit enables Save. A
    // `.gl-btn-reason` line keeps its info glyph and fills its text span.
    const reason = this.submitTargets[0]?.dataset.cardDirtyDisabledReason || DEFAULT_DISABLED_REASON
    this.hintTargets.forEach((el) => {
      const text = el.querySelector(".gl-btn-reason__text") || el
      text.textContent = clean ? reason : ""
      el.hidden = !clean
    })
  }

  // A11Y-1: a disabled control explains why. Every submit target carries its
  // reason (declared, else DEFAULT_DISABLED_REASON) as `data-gl-tooltip` plus —
  // unless its reason line describes it — an accessible name of
  // "<label> — <reason>" for exactly as long as the clean
  // state disables it, and loses both the moment an edit enables the button —
  // an enabled Save must not keep telling the user to change something first.
  _syncDisabledReason(el, clean) {
    const reason = el.dataset.cardDirtyDisabledReason || DEFAULT_DISABLED_REASON
    if (clean && this._describedByHint(el)) {
      el.setAttribute("data-gl-tooltip", reason)
    } else if (clean) {
      this._rememberAuthoredName(el, reason)
      const label = this._authoredNames.get(el) || this._labelOf(el)
      el.setAttribute("data-gl-tooltip", reason)
      el.setAttribute("aria-label", label ? `${label}${NAME_SEPARATOR}${reason}` : reason)
    } else {
      el.removeAttribute("data-gl-tooltip")
      this._restoreAuthoredName(el)
    }
  }

  // Whether a reason-line hint target is this button's description — the line
  // then carries the reason for assistive technology, never the name.
  _describedByHint(el) {
    const ids = (el.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean)
    return this.hintTargets.some((hint) => hint.id && ids.includes(hint.id))
  }

  // The name the markup gave the button before this controller touched it —
  // an icon-only row Save's own aria-label stays the base of the composed
  // name and comes back when the button is enabled. A server-painted
  // composition (the partial renders `disabled` Saves with the same
  // "<label> — <reason>") is recognised as ours, not authored, so it is never
  // doubled and is removed like any other applied reason.
  _rememberAuthoredName(el, reason) {
    this._authoredNames ||= new WeakMap()
    if (this._authoredNames.has(el)) return
    const existing = el.getAttribute("aria-label")
    const painted = `${this._labelOf(el)}${NAME_SEPARATOR}${reason}`
    this._authoredNames.set(el, existing === painted ? null : existing)
  }

  _restoreAuthoredName(el) {
    if (!this._authoredNames?.has(el)) return
    const authored = this._authoredNames.get(el)
    if (authored === null) {
      el.removeAttribute("aria-label")
    } else {
      el.setAttribute("aria-label", authored)
    }
    this._authoredNames.delete(el)
  }

  // The visible label: an <input type=submit> shows its value, a <button> its
  // text (the leading icon contributes none).
  _labelOf(el) {
    const raw = el instanceof HTMLInputElement ? el.value : el.textContent
    return (raw || "").replace(/\s+/g, " ").trim()
  }

  // A stable string fingerprint of every field's value.
  _serialize() {
    return this._fields().map((el) => this._tokenFor(el)).join("|")
  }

  _fields() {
    return fingerprintFields(this.element)
  }

  _tokenFor(el) {
    return fieldToken(el)
  }
}
